import { gql } from '@apollo/client';
import { constructSlug } from '@tac/logic/vehicle-variants';
import { createApolloClient, apolloAPIErrorHandler } from '@/utils/apollo-api/client';
import { checkUcsrSaferPick } from '@/utils/api';
import { type HsiycSearchVehiclesRecord } from '@/types/legacy-api/graphql';

type GetVehiclesInput = {
  make?: string;
  model?: string;
  year?: string;
  vehicleIds?: string[];
};

type GetVehiclesOutput = {
  vehicles?: HsiycSearchVehiclesRecord[];
  error?: unknown;
};

export const getVehicles = async ({ make, model, year, vehicleIds }: GetVehiclesInput): Promise<GetVehiclesOutput> => {
  try {
    const client = createApolloClient();
    const response = await client.query({
      variables: { year, make, model, vehicleIds },
      query: gql`
        query SearchVehicles($year: Int, $make: String, $model: String, $vehicleIds: [String]) {
          hsiycSearchVehiclesConnection(
            manufactureYear: $year
            vehicleMake: $make
            vehicleModel: $model
            vehicleIds: $vehicleIds
          ) {
            nodes {
              ancapRating
              badgeDescription
              derivedBodyStyle
              bodyStyle
              engineDisplacement
              fuelType
              image
              imageOverride
              isBaseOption
              isBlacklisted
              makeDescription
              modelDescription
              newPrice
              pPlateStatus
              privateMaxPrice
              privateMinPrice
              ratingProviderOverride
              redbookCode
              safetyFeaturesLinkedCount
              segment
              sequenceNumber
              series
              slug
              totalRecommendedSafetyFeatures
              transmission
              ucsrAggressivity
              ucsrDriver
              ucsrPrimarySafety
              ucsrRating
              vehicleDescription
              year
              uri
            }
          }
        }
      `,
    });

    // **BUSINESS LOGIC**
    // Get vehicles from response,
    // if the vehicle has a ucsr rating, check if it is less than 2,
    // if it is, return a new vehicle object with a ucsrSaferPick of false

    // NOTE: logic below is retained from the old API function (fetchPageVehicles)
    const vehicles = (response?.data?.hsiycSearchVehiclesConnection?.nodes ?? []).map(
      (vehicle: HsiycSearchVehiclesRecord) => {
        if (vehicle.ucsrRating && vehicle.ucsrRating >= 0) {
          return checkUcsrSaferPick(vehicle);
        }

        return vehicle;
      }
    );

    return { vehicles };
  } catch (err) {
    return { error: err };
  }
};

export const getVehicleVariants = async ({ make, model }: Pick<GetVehiclesInput, 'make' | 'model'>) => {
  try {
    const client = createApolloClient();
    const response = await client.query({
      variables: {
        make: make ? constructSlug(make) : make,
        model: model ? constructSlug(model) : model,
      },
      query: gql`
        query SearchVehicles($make: String!, $model: String!) {
          hsiycSearchVehiclesConnection(vehicleMake: $make, vehicleModel: $model) {
            nodes {
              redbookCode
              year
              vehicleDescription
              derivedBodyStyle
              bodyStyle
              transmission
              fuelType
              series
              badgeDescription
              engineDisplacement
              sequenceNumber
              isBaseOption
              slug
              safetyFeaturesLinkedCount
              totalRecommendedSafetyFeatures
              newPrice
              privateMinPrice
              privateMaxPrice
            }
          }
        }
      `,
    });

    const vehicles = response?.data?.hsiycSearchVehiclesConnection?.nodes ?? [];

    return vehicles;
  } catch (err) {
    return apolloAPIErrorHandler(err);
  }
};

export const getVehicleSearchFilters = async () => {
  try {
    const client = createApolloClient();
    const response = await client.query({
      query: gql`
        query HsiycGetVehicleFiltersConnection {
          hsiycGetVehicleFiltersConnection {
            nodes {
              bodytypes
              recommendedsafetyfeatures
              makesandmodels
            }
          }
        }
      `,
    });

    const [vehicleFilters] = response?.data?.hsiycGetVehicleFiltersConnection?.nodes ?? [];

    return vehicleFilters;
  } catch (err) {
    return apolloAPIErrorHandler(err);
  }
};
